import type * as React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@cloudscape-design/components/box';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import type { Currency, DailyAggregate } from '@etiquetachef/types';
import { formatCurrencyCents, formatInt } from './formatters';
import { latestPartnerRemit, sumPartnerSplitCommission } from './partnerRemit';

const CURRENCIES: readonly Currency[] = ['BRL', 'USD', 'EUR'];

/**
 * Card "Repasse de parceiros" do `/dashboard/finance`: tenants PARTNER ativos, repasse mensal
 * combinado por moeda e, quando houve, a comissão de parceiro do modelo DIRECT (paga por
 * split/connect × a pagar por fora).
 *
 * Origem: #743 — 2026-09-25 17:32 +0200 (F8 da #679, ativação pelo parceiro).
 *
 * Por quê: fica FORA do MRR de propósito — o parceiro cobra o cliente dele e nos repassa um
 * valor combinado (`total_remit_cents`); somar isso ao MRR misturaria receita que cobramos com
 * receita que outro cobra. Equivalente mensal (anual ÷ 12), a mesma régua da D15. O recebido
 * de fato está nos extratos do CRM.
 *
 * Contrato: só apresentação sobre a `series` de `DailyAggregate` já carregada pela página.
 * Repasse = RETRATO do último dia com `partnerRemit` (`latestPartnerRemit`); comissão = SOMA do
 * período (`sumPartnerSplitCommission`). `null` = "sem dado" (dia anterior à F8/A4), que é
 * diferente de zero. Moedas zeradas são omitidas; sem nenhuma, mostra `R$ 0,00`. Valores em
 * CENTAVOS, formatados por `formatCurrencyCents`.
 *
 * Usado por: `FinanceDashboardPage` (web `apps/admin`, super-admin).
 *
 * Dados: `/aggregations/daily/{yyyymmdd}.partnerRemit` e `.partnerSplitCommission`, escritos
 * por `aggregateDailyMetrics` (Functions); schema `DailyAggregateSchema` em
 * `packages/types/src/aggregations.ts`. Semântica de cada campo em `./partnerRemit.ts`.
 *
 * Issues: #743 (repasse), #753 (comissão do modelo DIRECT), #679 (D15 — régua do MRR).
 *
 * Histórico:
 *  - 2026-09-27 19:33 +0200 — (#753) bloco de comissão de parceiro DIRECT.
 */
export function PartnerRemittanceCard({
  series,
}: {
  series: readonly DailyAggregate[];
}): React.JSX.Element {
  const { t } = useTranslation();
  const remit = latestPartnerRemit(series);
  const currencies = remit ? CURRENCIES.filter((c) => remit.byCurrency[c] > 0) : [];
  // #753 — o parceiro do modelo DIRECT: nós cobramos e a comissão dele sai do
  // bruto (pelo split, ou a pagar por fora). Só aparece quando houve alguma.
  const split = sumPartnerSplitCommission(series);
  const splitCurrencies = split
    ? CURRENCIES.filter((c) => split[c].split + split[c].connect + split[c].payable > 0)
    : [];

  return (
    <Container
      header={
        <Header variant="h2" description={t('finance.partnerRemit.description')}>
          {t('finance.partnerRemit.title')}
        </Header>
      }
    >
      {remit === null ? (
        <Box color="text-status-inactive">{t('finance.partnerRemit.noData')}</Box>
      ) : (
        <SpaceBetween size="m">
          <div>
            <Box variant="h1">{formatInt(remit.activeTenants)}</Box>
            <Box fontSize="body-s" color="text-status-inactive">
              {t('finance.partnerRemit.activeTenants')}
            </Box>
          </div>
          <div>
            <Box variant="awsui-key-label">{t('finance.partnerRemit.monthly')}</Box>
            {currencies.length === 0 ? (
              <Box>{formatCurrencyCents(0, 'BRL')}</Box>
            ) : (
              <SpaceBetween direction="horizontal" size="m">
                {currencies.map((c) => (
                  <Box key={c} fontSize="heading-m">
                    {formatCurrencyCents(remit.byCurrency[c], c)}
                  </Box>
                ))}
              </SpaceBetween>
            )}
          </div>
          {splitCurrencies.length > 0 && split && (
            <div>
              <Box variant="awsui-key-label">{t('finance.partnerRemit.splitTitle')}</Box>
              <SpaceBetween size="xxs">
                {splitCurrencies.map((c) => (
                  <Box key={c}>
                    {formatCurrencyCents(split[c].split + split[c].connect, c)} {t('finance.partnerRemit.splitPaid')}
                    {' · '}
                    {formatCurrencyCents(split[c].payable, c)} {t('finance.partnerRemit.splitPayable')}
                  </Box>
                ))}
              </SpaceBetween>
            </div>
          )}
        </SpaceBetween>
      )}
    </Container>
  );
}
